import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { FiArrowLeft, FiEdit2, FiToggleLeft, FiToggleRight, FiClock, FiAward, FiTrash2 } from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';

const QuizDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [deleting, setDeleting] = useState(false);

  const { data: quiz, isLoading, refetch } = useQuery({
    queryKey: ['quiz-detail', id],
    queryFn: () => axios.get(`/api/quizzes/${id}`).then(res => res.data.quiz)
  });

  const toggleQuizStatus = async () => {
    try {
      await axios.put(`/api/quizzes/${id}/status`, {
        isActive: !quiz.isActive
      });
      toast.success(`Quiz ${!quiz.isActive ? 'activated' : 'deactivated'} successfully`);
      refetch();
    } catch (error) {
      toast.error('Failed to update quiz status');
    }
  };

  const handleDeleteQuiz = async () => {
    if (!window.confirm('Are you sure you want to delete this quiz? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`/api/quizzes/${id}`);
      toast.success('Quiz deleted successfully');
      navigate('/quizzes');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete quiz');
      setDeleting(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!quiz) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Quiz not found</p>
        <Link to="/quizzes" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">
          Back to Quizzes
        </Link>
      </div>
    );
  }

  // Only admins can view quiz details
  if (!isAdmin) {
    navigate('/quizzes');
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/quizzes"
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <FiArrowLeft className="mr-2" />
          Back to Quizzes
        </Link>
        
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{quiz.title}</h1>
            {quiz.description && (
              <p className="text-gray-600 mt-2">{quiz.description}</p>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={toggleQuizStatus}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
            >
              {quiz.isActive ? (
                <>
                  <FiToggleRight className="text-green-600" size={24} />
                  <span className="text-green-600 font-medium">Active</span>
                </>
              ) : (
                <>
                  <FiToggleLeft className="text-gray-600" size={24} />
                  <span className="text-gray-600 font-medium">Inactive</span>
                </>
              )}
            </button>
            <button
              onClick={handleDeleteQuiz}
              disabled={deleting}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <FiTrash2 size={20} />
              <span>Delete</span>
            </button>
          </div>
        </div>
      </div>

      {/* Rest of the component remains the same */}
      {/* Quiz Info */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quiz Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3">
            <FiClock className="text-gray-400" size={20} />
            <div>
              <p className="text-sm text-gray-600">Duration</p>
              <p className="font-medium">{quiz.duration} minutes</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <FiAward className="text-gray-400" size={20} />
            <div>
              <p className="text-sm text-gray-600">Total Points</p>
              <p className="font-medium">{quiz.totalPoints} points</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <FiEdit2 className="text-gray-400" size={20} />
            <div>
              <p className="text-sm text-gray-600">Questions</p>
              <p className="font-medium">{quiz.questions?.length || 0} questions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Questions section remains the same */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900">Questions & Answers</h2>
        
        {quiz.questions?.map((question, index) => (
          <div key={question._id} className="card">
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-medium text-gray-900">
                Question {index + 1}
                <span className="ml-2 text-sm text-gray-500">({question.points} points)</span>
              </h3>
            </div>
            
            <p className="text-gray-900 mb-4">{question.question}</p>
            
            <div className="space-y-2">
              {question.options.map((option, optionIndex) => (
                <div
                  key={optionIndex}
                  className={`p-3 rounded-lg flex items-start ${
                    optionIndex === question.correctAnswer
                      ? 'bg-green-50 border-2 border-green-300'
                      : 'bg-gray-50 border-2 border-transparent'
                  }`}
                >
                  <span className="mr-3 flex-shrink-0 font-medium">
                    {String.fromCharCode(65 + optionIndex)}.
                  </span>
                  <span className={optionIndex === question.correctAnswer ? 'font-medium text-green-800' : ''}>
                    {option}
                  </span>
                  {optionIndex === question.correctAnswer && (
                    <span className="ml-auto text-green-600 font-medium">
                      ✓ Correct Answer
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="mt-8 flex justify-between items-center">
        <Link
          to={`/leaderboard/quiz/${id}`}
          className="btn-secondary"
        >
          View Leaderboard
        </Link>
        
        <div className="text-sm text-gray-500">
          Created on {new Date(quiz.createdAt).toLocaleDateString()}
        </div>
      </div>
    </div>
  );
};

export default QuizDetail;